import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Grade as GradeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,

} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const GradeAssignment = ({ workflowData, onDataUpdate, onNext, onBack, isValid }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Local state
  const [splits, setSplits] = useState(workflowData.splits || []);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSplit, setEditingSplit] = useState(null);
  const [newSplit, setNewSplit] = useState({
    displayName: '',
    suffix: '',
    gradesServed: [],
    splitType: 'elementary'
  });

  // Available grades from parent school
  const availableGrades = workflowData.parentSchool?.enrollmentData?.by_grade 
    ? Object.keys(workflowData.parentSchool.enrollmentData.by_grade)
    : [];

  // Grade options for selection
  const gradeOptions = [
    'PK', 'KG', '01', '02', '03', '04', '05', '06', 
    '07', '08', '09', '10', '11', '12'
  ];

  // Split type options
  const splitTypeOptions = [
    { value: 'elementary', label: 'Elementary School', suffix: 'es' },
    { value: 'middle', label: 'Middle School', suffix: 'ms' },
    { value: 'high', label: 'High School', suffix: 'hs' },
    { value: 'upper', label: 'Upper Elementary', suffix: 'ue' },
    { value: 'lower', label: 'Lower Elementary', suffix: 'le' },
    { value: 'intermediate', label: 'Intermediate', suffix: 'im' },
    { value: 'junior', label: 'Junior High', suffix: 'jh' },
    { value: 'other', label: 'Other', suffix: 'ot' }
  ];

  // Update workflow data when splits change
  useEffect(() => {
    onDataUpdate({ splits });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits]);

  // Get already assigned grades
  const getAssignedGrades = (excludeSplitIndex = -1) => {
    return splits.reduce((assigned, split, index) => {
      if (index !== excludeSplitIndex) {
        return [...assigned, ...split.gradesServed];
      }
      return assigned;
    }, []);
  };

  // Get available grades for selection (not already assigned)
  const getUnassignedGrades = (excludeSplitIndex = -1) => {
    const assignedGrades = getAssignedGrades(excludeSplitIndex);
    return availableGrades.filter(grade => !assignedGrades.includes(grade));
  };

  // Handle adding a new split
  const handleAddSplit = () => {
    const splitTypeConfig = splitTypeOptions.find(opt => opt.value === newSplit.splitType);
    
    const split = {
      ...newSplit,
      suffix: splitTypeConfig?.suffix || 'ot',
      id: Date.now() // Temporary ID for tracking
    };

    setSplits(prev => [...prev, split]);
    setNewSplit({
      displayName: '',
      suffix: '',
      gradesServed: [],
      splitType: 'elementary'
    });
    setIsAddDialogOpen(false);
    enqueueSnackbar('Split school added successfully', { variant: 'success' });
  };

  // Handle editing a split
  const handleEditSplit = (index) => {
    setEditingSplit(index);
    setNewSplit({ ...splits[index] });
    setIsAddDialogOpen(true);
  };

  // Handle updating an existing split
  const handleUpdateSplit = () => {
    const splitTypeConfig = splitTypeOptions.find(opt => opt.value === newSplit.splitType);
    
    const updatedSplit = {
      ...newSplit,
      suffix: splitTypeConfig?.suffix || 'ot'
    };

    setSplits(prev => prev.map((split, index) => 
      index === editingSplit ? updatedSplit : split
    ));
    
    setEditingSplit(null);
    setNewSplit({
      displayName: '',
      suffix: '',
      gradesServed: [],
      splitType: 'elementary'
    });
    setIsAddDialogOpen(false);
    enqueueSnackbar('Split school updated successfully', { variant: 'success' });
  };

  // Handle removing a split
  const handleRemoveSplit = (index) => {
    setSplits(prev => prev.filter((_, i) => i !== index));
    enqueueSnackbar('Split school removed', { variant: 'info' });
  };

  // Handle grade selection
  const handleGradeToggle = (grade) => {
    setNewSplit(prev => ({
      ...prev,
      gradesServed: prev.gradesServed.includes(grade)
        ? prev.gradesServed.filter(g => g !== grade)
        : [...prev.gradesServed, grade].sort((a, b) => {
            const gradeOrder = ['PK', 'KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
            return gradeOrder.indexOf(a) - gradeOrder.indexOf(b);
          })
    }));
  };

  // Validate split before saving
  const validateSplit = () => {
    if (!newSplit.displayName.trim()) {
      enqueueSnackbar('School name is required', { variant: 'error' });
      return false;
    }
    
    if (newSplit.gradesServed.length === 0) {
      enqueueSnackbar('At least one grade must be selected', { variant: 'error' });
      return false;
    }

    // Check for grade conflicts
    const unassignedGrades = getUnassignedGrades(editingSplit);
    const conflictingGrades = newSplit.gradesServed.filter(grade => !unassignedGrades.includes(grade));
    
    if (conflictingGrades.length > 0) {
      enqueueSnackbar(`Grades ${conflictingGrades.join(', ')} are already assigned to another split`, { variant: 'error' });
      return false;
    }

    return true;
  };

  // Get enrollment total for a split
  const getSplitEnrollmentTotal = (gradesServed) => {
    if (!workflowData.parentSchool?.enrollmentData?.by_grade) return 0;
    
    return gradesServed.reduce((total, grade) => {
      const gradeData = workflowData.parentSchool.enrollmentData.by_grade[grade];
      return total + (gradeData?.total || 0);
    }, 0);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GradeIcon color="primary" />
        Assign Grades to Split Schools
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create split schools by assigning grade ranges. Each grade can only be assigned to one split school.
      </Typography>

      {/* Parent School Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Parent School: {workflowData.parentSchool?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            NCESSCH: {workflowData.parentNcessch}
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Available Grades with Enrollment:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableGrades.map(grade => {
                const enrollmentData = workflowData.parentSchool.enrollmentData.by_grade[grade];
                const isAssigned = getAssignedGrades().includes(grade);
                
                return (
                  <Tooltip 
                    key={grade}
                    title={`Total Enrollment: ${enrollmentData?.total || 0}`}
                  >
                    <Chip
                      label={`${grade} (${enrollmentData?.total || 0})`}
                      color={isAssigned ? 'success' : 'default'}
                      variant={isAssigned ? 'filled' : 'outlined'}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Current Splits */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Split Schools ({splits.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
              disabled={getUnassignedGrades().length === 0}
            >
              Add Split School
            </Button>
          </Box>

          {splits.length === 0 ? (
            <Alert severity="info">
              <AlertTitle>No Split Schools Created</AlertTitle>
              Click "Add Split School" to start creating your split configuration.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>School Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Grades Served</strong></TableCell>
                    <TableCell><strong>Est. Enrollment</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {splits.map((split, index) => (
                    <TableRow key={split.id || index}>
                      <TableCell>
                        <Typography variant="body2">
                          {split.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Suffix: -{split.suffix}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={splitTypeOptions.find(opt => opt.value === split.splitType)?.label || split.splitType}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {split.gradesServed.map(grade => (
                            <Chip 
                              key={grade} 
                              label={grade} 
                              size="small"
                              color="success"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getSplitEnrollmentTotal(split.gradesServed)}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditSplit(index)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveSplit(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Validation Warnings */}
      {availableGrades.length > 0 && getUnassignedGrades().length > 0 && splits.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Unassigned Grades</AlertTitle>
          The following grades have enrollment data but are not assigned to any split: {' '}
          {getUnassignedGrades().join(', ')}
        </Alert>
      )}

      {/* Add/Edit Split Dialog */}
      <Dialog 
        open={isAddDialogOpen} 
        onClose={() => {
          setIsAddDialogOpen(false);
          setEditingSplit(null);
          setNewSplit({
            displayName: '',
            suffix: '',
            gradesServed: [],
            splitType: 'elementary'
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSplit !== null ? 'Edit Split School' : 'Add Split School'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="School Name"
                value={newSplit.displayName}
                onChange={(e) => setNewSplit(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="e.g., Lincoln Elementary School"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>School Type</InputLabel>
                <Select
                  value={newSplit.splitType}
                  onChange={(e) => setNewSplit(prev => ({ ...prev, splitType: e.target.value }))}
                  label="School Type"
                >
                  {splitTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} (-{option.suffix})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Select Grades for this Split:
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Only grades with enrollment data and not already assigned are available.
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {gradeOptions.map(grade => {
                  const hasData = availableGrades.includes(grade);
                  const isAssigned = getAssignedGrades(editingSplit).includes(grade);
                  const isSelected = newSplit.gradesServed.includes(grade);
                  const isDisabled = !hasData || isAssigned;
                  
                  return (
                    <FormControlLabel
                      key={grade}
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleGradeToggle(grade)}
                          disabled={isDisabled}
                        />
                      }
                      label={
                        <Chip
                          label={grade}
                          size="small"
                          color={isSelected ? 'primary' : 'default'}
                          variant={hasData ? 'filled' : 'outlined'}
                          disabled={isDisabled}
                        />
                      }
                    />
                  );
                })}
              </Box>
              
              {newSplit.gradesServed.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated enrollment for selected grades: {getSplitEnrollmentTotal(newSplit.gradesServed)}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsAddDialogOpen(false);
            setEditingSplit(null);
            setNewSplit({
              displayName: '',
              suffix: '',
              gradesServed: [],
              splitType: 'elementary'
            });
          }}>
            Cancel
          </Button>
          <Button 
            onClick={editingSplit !== null ? handleUpdateSplit : handleAddSplit}
            variant="contained"
            disabled={!validateSplit}
          >
            {editingSplit !== null ? 'Update Split' : 'Add Split'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Next Step Button */}
      {isValid && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onNext}
            size="large"
          >
            Continue to Address Assignment
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GradeAssignment;
